import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';

import {
  INCENTIVES_MARKET_CONTRACT_UNIT_TAGS,
  LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS,
} from 'modules/incentivesMarket/constants/incentivesMarketContractTemplateTags';
import {
  PublishIncentiveOfferDraft,
  PublisherPartner,
} from 'modules/incentivesMarket/modals/PublishIncentiveOffer/types/PublishIncentiveOfferModalContextType';
import { IncentiveOfferType } from 'modules/incentivesMarket/types';
import {
  replaceIncentivesMarketContractTag,
  replaceIncentivesMarketContractUnitTag,
} from 'modules/incentivesMarket/utils/replaceIncentivesMarketContractTag';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import { getPublisherAddressLine } from './getPublisherAddressLine';

const DATE_LOCALES: Record<string, Locale> = {
  es,
  en: enUS,
  fr,
};

const PENDING = '-';

export type ApplyIncentivesMarketPublishContractPreviewTagsArgs = {
  html: string;
  offerType: IncentiveOfferType;
  publisherPartner: PublisherPartner | null;
  incentiveOffer: PublishIncentiveOfferDraft | null;
  language: string;
  operationReferencePlaceholder: string;
};

export const applyIncentivesMarketPublishContractPreviewTags = ({
  html,
  offerType,
  publisherPartner,
  incentiveOffer,
  language,
  operationReferencePlaceholder,
}: ApplyIncentivesMarketPublishContractPreviewTagsArgs): string => {
  const langKey = language?.split('-')[0] || 'es';
  const dateLocale = DATE_LOCALES[langKey] || es;

  const sharesTotal = incentiveOffer?.unitsCount?.total ?? 0;
  const unitPrice = incentiveOffer?.unitPrice ?? 0;
  const totalPrice = sharesTotal * unitPrice;
  const shareClassLabel = incentiveOffer?.className || '-';

  const publisherName = publisherPartner?.name?.trim() || PENDING;
  const publisherCif = publisherPartner?.cif?.trim() || PENDING;
  const publisherAddress =
    getPublisherAddressLine(publisherPartner) || PENDING;
  const publisherIban =
    publisherPartner?.bankAccountNumber?.trim() || PENDING;

  const isSell = offerType === IncentiveOfferType.SELL;

  const sellerName = isSell ? publisherName : PENDING;
  const sellerCif = isSell ? publisherCif : PENDING;
  const sellerAddress = isSell ? publisherAddress : PENDING;
  const sellerIban = isSell ? publisherIban : PENDING;
  const sellerAccountHolder = isSell ? publisherName : PENDING;

  const buyerName = isSell ? PENDING : publisherName;
  const buyerCif = isSell ? PENDING : publisherCif;
  const buyerAddress = isSell ? PENDING : publisherAddress;

  let result = html;

  result = replaceIncentivesMarketContractTag(result, 'MERCADO_NOMBRE_VENDEDOR', sellerName);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DNI_VENDEDOR', sellerCif);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DOMICILIO_VENDEDOR', sellerAddress);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_IBAN_VENDEDOR', sellerIban);
  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_TITULAR_CUENTA_VENDEDOR',
    sellerAccountHolder
  );

  result = replaceIncentivesMarketContractTag(result, 'MERCADO_NOMBRE_COMPRADOR', buyerName);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DNI_COMPRADOR', buyerCif);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DOMICILIO_COMPRADOR', buyerAddress);

  result = replaceIncentivesMarketContractUnitTag(
    result,
    INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.NUM,
    LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.NUM,
    formatNumber(sharesTotal) || PENDING
  );
  result = replaceIncentivesMarketContractUnitTag(
    result,
    INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.CLASS,
    LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.CLASS,
    shareClassLabel
  );
  result = replaceIncentivesMarketContractUnitTag(
    result,
    INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.PRICE,
    LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.PRICE,
    formatCurrencyDecimals(unitPrice) || PENDING
  );
  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_PRECIO_TOTAL',
    formatCurrencyDecimals(totalPrice) || PENDING
  );

  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_FECHA',
    format(new Date(), 'PPP', { locale: dateLocale })
  );
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_HORA', format(new Date(), 'HH:mm'));
  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_REFERENCIA_OPERACION',
    operationReferencePlaceholder
  );

  return result;
};
